// 파일 화면(설계서 §9.4 "파일 창") — **2단**(왼쪽 내 PC · 오른쪽 원격) 화면.
// 로직은 `useFilePanel` 에 있고 이 부품은 그리기만 한다.
//
// 놓이는 곳이 둘이다(2026-09-29 사용자 결정 — 파일 화면은 **별도 창**):
//   - `window`: 파일 창(files.html)을 가득 채운다. 전송 진행·알림도 창 아래에 보인다(세션 창 도구 막대가 안 보일 수 있다).
//   - `stage` : 창을 띄우지 못했을 때(브라우저 팝업 차단) 원격 화면 아래쪽에 대신 보인다. 진행·알림은 도구 막대가 보인다.
//
// 🔴 `window.prompt`·`confirm` 을 쓰지 않는다 — 통합앱(WebView2)에서는 `prompt()` 가 동작하지 않아
//    브라우저에서만 되는 화면이 된다. 새 폴더 이름 칸과 삭제 확인을 패널 안에 직접 그린다.
import { useState } from 'react';
import { formatBytes } from '../files/protocol';
import { describeStats, panelStats, rootLabel, selectModeOf } from '../files/panelView';
import { pickLocalFiles } from '../files/source';
import type { FilePanel as Panel, PanelSide, Side } from '../files/useFilePanel';
import { stageIsolationProps } from './stageIsolation';
import { TransferStatus } from './TransferStatus';
import { IS_WINDOWS_VIEWER } from '../clipboard/useClipboardShare';

/** 수정 시각 — 목록이 빽빽하므로 짧게(연-월-일 시:분). */
function fmtTime(ms?: number): string {
  if (!ms) {
    return '';
  }
  const d = new Date(ms);
  const p = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())} ${p(d.getHours())}:${p(d.getMinutes())}`;
}

/**
 * @param panel   파일 화면 상태·동작(세션 창 = `useFilePanel` · 파일 창 = 그 상태를 받아 되돌려 보내는 구현)
 * @param refocus 버튼을 누른 뒤 포커스를 돌려줄 곳(세션 창 = 키보드 캡처 칸)
 * @param place   놓이는 곳 — 위 머리말
 * @param banner  위쪽 안내(창을 못 띄워 화면 안에 연 이유 등)
 */
export function FilePanel({
  panel,
  refocus,
  place = 'stage',
  banner,
}: {
  panel: Panel;
  refocus: () => void;
  place?: 'stage' | 'window';
  banner?: { text: string; onClose?: () => void };
}) {
  /** 새 폴더 이름을 받는 칸이 열린 쪽(없으면 닫힘). */
  const [naming, setNaming] = useState<Side | null>(null);
  const [name, setName] = useState('');
  /** 삭제 확인이 열린 쪽 — 지우기는 되돌릴 수 없으므로 한 번 더 묻는다. */
  const [confirming, setConfirming] = useState<Side | null>(null);

  if (!panel.open) {
    return null;
  }

  const closeForms = () => {
    setNaming(null);
    setName('');
    setConfirming(null);
  };

  const submitName = (side: Side) => {
    const trimmed = name.trim();
    if (trimmed) {
      panel.mkdir(side, trimmed);
    }
    closeForms();
  };

  const column = (side: Side, state: PanelSide, title: string) => {
    const selected = state.selected.length;
    return (
      <div className="fpanel-col">
        <div className="fpanel-head">
          <strong style={{ fontSize: 12 }}>{title}</strong>
          {/* 드라이브 선택(2026-09-29 사용자 요구) — 위로 올라가지 않고 바로 옮긴다. 최상위면 "이 기기" 가 보인다. */}
          {state.roots.length > 0 && (
            <select
              className="fpanel-drive"
              title="드라이브"
              aria-label={`${title} 드라이브`}
              value={state.root}
              disabled={!state.ready}
              onChange={(e) => e.target.value && panel.openRoot(side, e.target.value)}
            >
              {!state.root && <option value="">이 기기</option>}
              {state.roots.map((r) => (
                <option key={r} value={r}>
                  {rootLabel(r)}
                </option>
              ))}
            </select>
          )}
          <button className="ghost" title="위로" disabled={!state.canUp} onClick={() => panel.up(side)}>
            ↑
          </button>
          <button className="ghost" title="새로 고침" disabled={!state.ready} onClick={() => panel.refresh(side)}>
            ⟳
          </button>
          <button
            className="ghost"
            title="새 폴더 만들기"
            aria-label="새 폴더 만들기"
            disabled={!state.ready}
            onClick={() => {
              closeForms();
              setNaming(side);
            }}
          >
            {/* 폴더 그림 + "생성"(2026-09-29 사용자 요구 — 옆의 🗑 삭제와 같은 모양). */}
            📁 <span className="bar-label">생성</span>
          </button>
          <button
            className="ghost"
            title="고른 항목 삭제"
            disabled={selected === 0}
            onClick={() => {
              closeForms();
              setConfirming(side);
            }}
          >
            🗑 <span className="bar-label">삭제</span>
          </button>
        </div>

        <div className="fpanel-path" title={state.label}>
          {state.ready ? state.label || '이 기기' : '열린 폴더 없음'}
        </div>

        {naming === side && (
          <div className="fpanel-form">
            <input
              autoFocus
              value={name}
              placeholder="새 폴더 이름"
              onChange={(e) => setName(e.target.value)}
              onKeyDown={(e) => {
                e.stopPropagation(); // 원격으로 키가 새지 않게
                if (e.key === 'Enter') submitName(side);
                if (e.key === 'Escape') closeForms();
              }}
            />
            <button className="ghost" onClick={() => submitName(side)}>
              만들기
            </button>
            <button className="ghost" onClick={closeForms}>
              취소
            </button>
          </div>
        )}

        {confirming === side && (
          <div className="fpanel-form">
            <span>고른 {selected}개를 지웁니다. 되돌릴 수 없습니다.</span>
            <button
              className="ghost"
              onClick={() => {
                panel.remove(side);
                closeForms();
              }}
            >
              지우기
            </button>
            <button className="ghost" onClick={closeForms}>
              취소
            </button>
          </div>
        )}

        {state.error && <div className="fpanel-msg">{state.error}</div>}

        {/* 목록의 빈 곳을 누르면 선택 해제(탐색기와 같다) — 항목 클릭은 아래에서 전파를 끊는다. */}
        <div className="fpanel-list" onClick={() => panel.clearSelection(side)}>
          {!state.ready && side === 'local' && (
            <div className="fpanel-empty">
              {panel.canPickLocal ? (
                <>
                  내 PC 의 폴더를 열면 여기에 보입니다.{' '}
                  <button
                    className="ghost"
                    onClick={() => {
                      panel.pickLocalFolder();
                      refocus();
                    }}
                  >
                    📂 폴더 열기
                  </button>
                </>
              ) : (
                // Safari·Firefox — 보내기는 위 버튼으로 된다. 원격에서 받기(내 PC 폴더에 쓰기)만 안 된다.
                '이 브라우저는 내 PC 폴더를 열 수 없습니다. 보내기는 위 [📄 파일 보내기…]·[📁 폴더 보내기…]로 하세요(원격에서 받기는 Chrome·Edge 또는 통합앱).'
              )}
            </div>
          )}
          {state.loading && <div className="fpanel-empty">읽는 중…</div>}
          {state.ready && !state.loading && state.items.length === 0 && <div className="fpanel-empty">빈 폴더입니다.</div>}
          {state.items.map((it) => (
            <div
              key={it.name}
              className={`fpanel-row${state.selected.includes(it.name) ? ' on' : ''}`}
              title={it.dir ? '두 번 누르면 엽니다' : it.name}
              onClick={(e) => {
                e.stopPropagation();
                panel.select(side, it.name, selectModeOf(e));
              }}
              onDoubleClick={() => it.dir && panel.openDir(side, it.name)}
            >
              <span>{it.dir ? '📁' : '📄'}</span>
              <span className="fpanel-name">{it.name}</span>
              <span className="fpanel-size">{it.dir ? '' : formatBytes(it.size)}</span>
              <span className="fpanel-size">{fmtTime(it.mtime)}</span>
            </div>
          ))}
        </div>
        {/* 상태 표시줄(2026-09-29 사용자 요구) — 이 폴더 전체 수·용량 / 고른 수·용량. 목록이 창 바닥에 바로 붙지 않게 하는 구분선도 겸한다. */}
        <div className="fpanel-foot" aria-live="polite">
          {state.ready && (() => {
            const t = describeStats(panelStats(state.items, state.selected));
            return (
              <>
                <span>{t.all}</span>
                <span className="grow" />
                <span className={t.picked ? 'fpanel-picked' : undefined}>{t.picked}</span>
              </>
            );
          })()}
        </div>
      </div>
    );
  };

  const canUpload = panel.local.ready && panel.remote.ready && panel.local.selected.length > 0 && !panel.busy;
  const canDownload = panel.local.ready && panel.remote.ready && panel.remote.selected.length > 0 && !panel.busy;
  // [파일 보내기…]·[폴더 보내기…] — 원격 칸에 연 폴더가 있어야 보낼 곳이 정해진다.
  const canSendPicked = panel.remote.ready && !panel.busy;

  /** 선택 창은 **이 클릭 안에서** 열어야 한다(브라우저 규칙) — 고른 뒤 원격의 연 폴더로 보낸다. */
  const pickAndSend = (folder: boolean) => {
    void pickLocalFiles(folder).then((picked) => panel.sendPicked(picked, folder));
    refocus();
  };

  return (
    // 🔴 패널 안의 클릭·마우스는 원격 화면(무대)으로 내려가면 안 된다 — 무대는 클릭을 키보드 캡처 칸으로,
    //    마우스를 원격 입력으로 넘긴다. 패널에서 이름을 입력하는 동안 포커스를 빼앗기지도 않게 여기서 끊는다.
    //    (같은 차단이 채팅 패널에도 필요해 `stageIsolationProps` 로 공통화했다 — 거기서 한 종류를 빠뜨려 결함이 났다.)
    // `plat-win` = Windows 뷰어 — 버튼·글자를 작게(2026-09-29 사용자 요구, 세션 도구 막대와 같은 규칙).
    // 🔴 화면 안 자리의 클래스는 `in-stage` — `stage` 로 두면 원격 화면 무대의 `.stage` 규칙(가운데 정렬 등)을 같이 받아
    //    두 칸이 폭 0 으로 접힌다(2026-09-29 칸 폭 컨테이너를 넣으며 드러남).
    <div className={`fpanel ${place === 'stage' ? 'in-stage' : 'window'}${IS_WINDOWS_VIEWER ? ' plat-win' : ''}`} {...stageIsolationProps}>
      {banner && (
        <div className="fpanel-banner" role="status">
          <span>{banner.text}</span>
          {banner.onClose && (
            <button className="clip-toast-close" aria-label="닫기" onClick={banner.onClose}>
              ×
            </button>
          )}
        </div>
      )}
      <div className="fpanel-bar">
        <strong>🗂 파일</strong>
        <span className="fpanel-size">고른 항목을 가운데 화살표로 옮깁니다. 여러 개는 Ctrl(⌘)·Shift+클릭, 폴더는 두 번 눌러 엽니다.</span>
        <span className="grow" />
        {panel.canSendPicked && (
          <>
            <button
              className="ghost"
              disabled={!canSendPicked}
              title="내 PC 에서 파일을 골라 원격의 연 폴더로 보냅니다(여러 개를 한 번에 고를 수 있습니다)"
              onClick={() => pickAndSend(false)}
            >
              📄 <span className="bar-label">파일 보내기…</span>
            </button>
            <button
              className="ghost"
              disabled={!canSendPicked}
              title="내 PC 에서 폴더를 골라 통째로(하위 폴더 포함) 원격의 연 폴더로 보냅니다"
              onClick={() => pickAndSend(true)}
            >
              📁 <span className="bar-label">폴더 보내기…</span>
            </button>
          </>
        )}
        <button
          className="ghost"
          onClick={() => {
            panel.setOpen(false);
            closeForms();
            refocus();
          }}
        >
          닫기
        </button>
      </div>
      <div className="fpanel-cols">
        {column('local', panel.local, '내 PC')}
        <div className="fpanel-mid">
          <button
            className="ghost"
            title="내 PC 에서 고른 것을 원격의 연 폴더로 보냅니다"
            disabled={!canUpload}
            onClick={() => {
              panel.upload();
              refocus();
            }}
          >
            <span className="bar-label">보내기 </span>→
          </button>
          <button
            className="ghost"
            title="원격에서 고른 것을 내 PC 의 연 폴더로 받습니다"
            disabled={!canDownload}
            onClick={() => {
              panel.download();
              refocus();
            }}
          >
            ← <span className="bar-label">받기</span>
          </button>
        </div>
        {column('remote', panel.remote, '원격')}
      </div>
      {/* 파일 창에서는 전송 진행·결과를 창 안에 보인다(세션 창 도구 막대는 이 창 뒤에 가려져 있을 수 있다). */}
      {place === 'window' && (panel.transfer || panel.notice) && (
        <div className="fpanel-status">
          <TransferStatus
            transfer={panel.transfer}
            notice={panel.notice}
            onCancel={panel.cancel}
            onDismiss={panel.dismissNotice}
          />
        </div>
      )}
    </div>
  );
}
